import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { CompProps, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@neo/primitives/collapsible'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { AudioPrompt } from '../components/audio-prompt'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { HoldMusicField, type HoldMusicValue } from '../components/hold-music-field'
import { NodevarList } from '../components/nodevar-list'
import { RecordingConfig } from '../components/recording-config'
import { promptData } from '../config/nodes.config'
import { v2validations } from '../config/validation.config'
import type { ConferenceBridgeNodeData, SidebarComponentProps } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = ConferenceBridgeNodeData['config']

const conferenceEventTypes = [
  { value: 'all', label: 'All Events' },
  { value: 'member_enter', label: 'Member Enter' },
  { value: 'member_exit', label: 'Member Exit' },
  { value: 'member_speak', label: 'Member Speak' },
  { value: 'recording', label: 'Recording' },
  { value: 'digits', label: 'Digits' },
  { value: 'conference_end', label: 'Conference End' },
]

const chimeOptions = [
  { value: 'none', label: 'None' },
  { value: 'beep', label: 'Beep' },
  { value: 'tone', label: 'Tone' },
]

type ConferenceBridgeInnerType = ReturnType<
  ReturnType<typeof v2validations>['conference_bridge']['innerType']
>

const getAdditionalSettingsFields = (
  validations: ConferenceBridgeInnerType,
): GenericFieldConfig<FormValues> => [
  {
    name: 'pin_entry_tries',
    label: 'PIN entry tries',
    zodValidation: validations.shape.pin_entry_tries,
    componentType: 'inputNumber',
    inputProps: {
      type: 'number',
      min: 1,
      max: 10,
      placeholder: 'Enter PIN entry tries',
    },
  },
  {
    name: 'entry_chime',
    label: 'Entry chime',
    zodValidation: validations.shape.entry_chime,
    componentType: 'select',
    inputProps: {
      options: chimeOptions,
      placeholder: 'Select chime',
    },
  },
  {
    name: 'exit_chime',
    label: 'Exit chime',
    zodValidation: validations.shape.exit_chime,
    componentType: 'select',
    inputProps: {
      options: chimeOptions,
      placeholder: 'Select chime',
    },
  },
  {
    name: 'join_muted',
    label: 'Join muted',
    zodValidation: validations.shape.join_muted,
    componentType: 'checkbox',
  },
  {
    name: 'hold_callers',
    label: 'Hold callers until moderator joins',
    zodValidation: validations.shape.hold_callers,
    componentType: 'checkbox',
  },
]

const AdditionalSettingsSection = ({
  form,
  validations,
}: {
  form: GenericFormType<FormValues>
  validations: ConferenceBridgeInnerType
}) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="border rounded-md p-3">
      <CollapsibleTrigger className="flex items-center justify-between w-full">
        <span className="text-s-500">Additional Settings</span>
        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-3 space-y-3">
        <FieldsRenderer form={form} fields={getAdditionalSettingsFields(validations)} />
      </CollapsibleContent>
    </Collapsible>
  )
}

const fields = (nodeVars: string[]): GenericFieldConfig<FormValues> => {
  const validations = v2validations({ nodeVars }).conference_bridge.innerType()

  return [
    {
      name: 'name',
      label: 'Name',
      zodValidation: validations.shape.name,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter name',
      },
    },
    {
      name: 'conference_name',
      label: 'Conference name',
      zodValidation: validations.shape.conference_name,
      componentType: 'input',
      inputProps: {
        placeholder: 'Enter conference name',
        endIcon: (
          <NodevarList
            nodevars={nodeVars}
            className="pr-2"
            inputElementSelector="[name=conference_name]"
          />
        ),
      },
    },
    {
      name: 'conference_type',
      label: 'Conference type',
      componentType: 'radioGroup',
      inputProps: {
        options: [
          { value: 'custom', label: 'Open' },
          { value: 'pinned', label: 'Protected (PIN)' },
        ],
      },
    },
    {
      name: 'participant_pin',
      label: 'Participant PIN',
      componentType: 'input',
      visible: (state) => state.conference_type === 'pinned',
      inputProps: {
        placeholder: 'Enter participant PIN',
        type: 'password',
      },
    },
    {
      name: 'mod_pin',
      label: 'Moderator PIN',
      componentType: 'input',
      visible: (state) => state.conference_type === 'pinned',
      inputProps: {
        placeholder: 'Enter moderator PIN',
        type: 'password',
      },
    },
    // Recording section
    {
      name: 'record',
      type: 'custom',
      component: ({ value, form }: CompProps<FormValues>) => (
        <RecordingConfig
          value={{
            record: (value as boolean) ?? false,
            record_mode: form.getFieldValue('record_mode') ?? 'always',
            record_condition: form.getFieldValue('record_condition') ?? '',
          }}
          onChange={(val) => {
            form.setFieldValue('record', val.record)
            form.setFieldValue('record_mode', val.record_mode)
            form.setFieldValue('record_condition', val.record_condition)
          }}
          nodeVars={nodeVars}
          showRecordingOptions={false}
        />
      ),
      onChangeListenTo: ['record_mode', 'record_condition'],
    },
    // Announcement audio
    {
      name: 'announcement',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="announcement">
            {(field) => (
              <AudioPrompt
                label="Announcement"
                value={field.state.value ?? promptData}
                arrayIndex={0}
                onChange={(data) => field.handleChange(data)}
                nodeVars={nodeVars}
              />
            )}
          </form.Field>
        </div>
      ),
    },
    // Hold music
    {
      name: 'hold_prompt',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="hold_prompt">
            {(field) => (
              <HoldMusicField
                label="Hold music"
                value={field.state.value as HoldMusicValue}
                onChange={(val) => field.handleChange(val)}
                nodeVars={nodeVars}
                showNodeVars
              />
            )}
          </form.Field>
        </div>
      ),
    },
    // Additional settings slot
    {
      name: 'additional_settings',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <AdditionalSettingsSection form={form} validations={validations} />
        </div>
      ),
    },
    // Events section
    {
      name: 'events',
      type: 'slot',
      element: ({ form }) => (
        <div className="col-span-2">
          <form.Field name="events">
            {(field) => (
              <EventCallbacks
                events={(field.state.value as EventCallback[]) ?? []}
                onChange={(events) => field.handleChange(events)}
                eventTypes={conferenceEventTypes}
                nodeVars={nodeVars}
                className="mt-4"
              />
            )}
          </form.Field>
        </div>
      ),
    },
  ]
}

export const ConferenceBridgeSidebarComponent = (
  props: SidebarComponentProps<ConferenceBridgeNodeData>,
) => {
  const { nodeVars } = props
  const formFields = fields(nodeVars)

  return <GenericSidebarComponent fields={formFields} {...props} />
}
